import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { ApiError, type ApiIssue } from "../api/errors";
import { productsApi } from "../api/products";
import type { Product, Revision } from "../api/types";
import {
  AUDIENCES,
  AUDIENCE_LABELS,
  FAMILY_LABELS,
  GLASSES_TYPES,
  GLASSES_TYPE_LABELS,
  categoryFor,
  familyForCategory,
  glassesTypeForCategory,
  isAudience,
  isFrameShape,
  type Audience,
  type FrameShape,
  type GlassesType,
} from "../catalog/taxonomy";
import { Panel } from "../components/ui";
import { Choice } from "./add/ClassificationStep";
import { FrameShapeSelect } from "./add/FrameShapeSelect";
import { formatMinorForInput, parseMoneyToMinor } from "./money";
import { issuesByField, useProductRefresh } from "./useProduct";

/**
 * Exactly the fields PATCH /draft accepts. Nothing is invented: 'audience' and
 * 'product_code' live on the product rather than the revision, but the draft
 * endpoint takes both, so both are edited here alongside the rest.
 *
 * Classification is held as taxonomy values, not as free text. `category` is
 * never typed: for glasses it is derived from the glasses type through
 * categoryFor, which is the same function the create flow uses, so a product
 * cannot end up filed under a category the phone does not recognise. A product
 * whose family is not glasses keeps the category it already has — this form
 * does not invent a contract it has not been given.
 */
interface FormState {
  name: string;
  product_code: string;
  description: string;
  glassesType: GlassesType | null;
  frameShape: FrameShape | null;
  audience: Audience | null;
  base_price: string;
  sale_price: string;
  currency: string;
}

function initialState(draft: Revision, product: Product): FormState {
  const shape = draft.frame_shape ?? "";
  const who = product.audience ?? "";
  return {
    name: draft.name ?? "",
    product_code: product.product_code ?? "",
    description: draft.description ?? "",
    glassesType: glassesTypeForCategory(draft.category ?? product.category),
    frameShape: isFrameShape(shape) ? shape : null,
    audience: isAudience(who) ? who : null,
    base_price: formatMinorForInput(draft.base_price),
    sale_price: formatMinorForInput(draft.sale_price),
    currency: draft.currency,
  };
}

export function MetadataForm({
  productId,
  product,
  draft,
}: {
  productId: string;
  product: Product;
  draft: Revision;
}) {
  const refresh = useProductRefresh(productId);
  const [form, setForm] = useState<FormState>(() => initialState(draft, product));
  const [saved, setSaved] = useState(false);

  // Re-seed when the server's draft moves under us (a reload after a conflict,
  // or another mutation bumping the version). Keyed on version so a user's
  // in-progress typing is not wiped by an unrelated refetch.
  useEffect(() => {
    setForm(initialState(draft, product));
    setSaved(false);
  }, [draft.version, draft.id, product.audience]); // eslint-disable-line react-hooks/exhaustive-deps

  const pristine = useMemo(
    () => JSON.stringify(form) === JSON.stringify(initialState(draft, product)),
    [form, draft, product],
  );

  const family = familyForCategory(draft.category ?? product.category);
  const isGlasses = family === "glasses";

  const save = useMutation({
    mutationFn: async () => {
      const basePrice = parseMoneyToMinor(form.base_price);
      const salePrice = form.sale_price.trim()
        ? parseMoneyToMinor(form.sale_price)
        : null;

      return productsApi.updateDraft(productId, {
        expected_version: draft.version,
        name: form.name.trim(),
        product_code: form.product_code.trim(),
        description: form.description.trim() || null,
        // Glasses derive their category from the type through the same helper
        // the create flow uses. Any other family keeps what it already had:
        // this form has no contract for one and must not guess at it.
        category: isGlasses
          ? categoryFor("glasses", form.glassesType)
          : (draft.category ?? product.category),
        frame_shape: isGlasses ? form.frameShape : null,
        audience: isGlasses ? form.audience : null,
        // The backend rejects a null base price; leaving the field unusable is
        // its call to make, so an unparseable value is sent as-is only when it
        // parsed, and otherwise omitted so the server reports the real problem.
        ...(basePrice === null ? {} : { base_price: basePrice }),
        sale_price: salePrice,
        currency: form.currency.trim().toUpperCase(),
      });
    },
    onSuccess: async () => {
      setSaved(true);
      // The response carries the new draft version; reloading the product is
      // what puts it into every other section on the page.
      await refresh();
    },
    onError: async (error) => {
      // A stale version means the local copy is provably behind. Reload so the
      // next attempt carries a token the server will accept — but never resend
      // automatically, because the administrator has to see what changed first.
      if (error instanceof ApiError && error.isStale) await refresh();
    },
  });

  const fieldIssues = issuesByField(save.error);
  const generalIssues = fieldIssues.get("") ?? [];

  // Obvious input mistakes only — the backend stays authoritative on money
  // rules such as sale price never exceeding base price.
  const localPriceError =
    form.base_price.trim() && parseMoneyToMinor(form.base_price) === null
      ? "Enter an amount such as 425.00"
      : null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (localPriceError || save.isPending) return;
    save.mutate();
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  /** The taxonomy controls, which set a typed value rather than a string. */
  function setChoice(patch: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
    setSaved(false);
  }

  return (
    <Panel
      title="Product details"
      actions={
        <>
          {!pristine ? <span className="muted">Unsaved changes</span> : null}
          {saved && pristine ? (
            <span className="muted">Saved</span>
          ) : null}
          <button
            type="button"
            className="btn btn--sm"
            disabled={pristine || save.isPending}
            onClick={() => {
              setForm(initialState(draft, product));
              save.reset();
            }}
          >
            Discard changes
          </button>
          <button
            type="submit"
            form="metadata-form"
            className="btn btn--primary btn--sm"
            disabled={pristine || save.isPending || Boolean(localPriceError)}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form id="metadata-form" onSubmit={onSubmit} className="stack">
        {save.isError ? <SaveError error={save.error} issues={generalIssues} /> : null}

        <div className="form-grid">
          <Field label="Name" issues={fieldIssues.get("name")}>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              aria-invalid={fieldIssues.has("name")}
              aria-label="Name"
            />
          </Field>

          <Field label="Product code" issues={fieldIssues.get("product_code")}>
            <input
              value={form.product_code}
              onChange={(e) => set("product_code", e.target.value)}
              aria-invalid={fieldIssues.has("product_code")}
              aria-label="Product code"
            />
          </Field>

          <Field
            label="Base price"
            issues={fieldIssues.get("base_price")}
            error={localPriceError}
          >
            <input
              value={form.base_price}
              inputMode="decimal"
              onChange={(e) => set("base_price", e.target.value)}
              aria-invalid={Boolean(localPriceError) || fieldIssues.has("base_price")}
              aria-label="Base price"
            />
          </Field>

          <Field label="Sale price" issues={fieldIssues.get("sale_price")}>
            <input
              value={form.sale_price}
              inputMode="decimal"
              placeholder="No sale price"
              onChange={(e) => set("sale_price", e.target.value)}
              aria-invalid={fieldIssues.has("sale_price")}
              aria-label="Sale price"
            />
          </Field>

          <Field label="Currency" issues={fieldIssues.get("currency")}>
            <input
              value={form.currency}
              maxLength={3}
              onChange={(e) => set("currency", e.target.value)}
              aria-invalid={fieldIssues.has("currency")}
              aria-label="Currency"
            />
          </Field>

          <div className="form-grid__full">
            <Field label="Description" issues={fieldIssues.get("description")}>
              <textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                aria-label="Description"
              />
            </Field>
          </div>
        </div>

        {/* Classification, in the vocabulary the phone filters by. These were
            free-text boxes: an administrator could type "Cat Eye", "cateye" or
            anything else, and the product then failed to appear under any shape
            filter with nothing on screen explaining why. */}
        {isGlasses ? (
          <>
            <Choice
              legend="Who are they for?"
              name="metadata-audience"
              options={AUDIENCES}
              labels={AUDIENCE_LABELS}
              value={form.audience}
              onChange={(audience) => setChoice({ audience })}
            />
            <Choice
              legend="Sunglasses or eyeglasses?"
              name="metadata-glasses-type"
              options={GLASSES_TYPES}
              labels={GLASSES_TYPE_LABELS}
              value={form.glassesType}
              onChange={(glassesType) => setChoice({ glassesType })}
            />
            <FrameShapeSelect
              value={form.frameShape}
              onChange={(frameShape) => setChoice({ frameShape })}
            />
          </>
        ) : (
          /* The other two families are described by the commerce fields above
             and nothing else. A frame shape, an audience and a try-on setting
             all describe frames; a cosmetic lens additionally has no
             prescription — it is a colour choice, not a medical device — so
             there is nothing more to ask here. Colour and stock are edited
             beside the photographs, under Colors, where the family's own
             contract lives. */
          <p className="muted" data-testid="family-contract">
            {family
              ? `${FAMILY_LABELS[family]} products are described by the details ` +
                "above. Frame shape, audience and try-on describe glasses, so " +
                "they are not asked here."
              : "This product is not classified. Its category is not one this " +
                "application knows, so it is left exactly as it is."}
          </p>
        )}
      </form>
    </Panel>
  );
}

function Field({
  label,
  issues,
  error,
  children,
}: {
  label: string;
  issues?: ApiIssue[];
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label>{label}</label>
      {children}
      {error ? <p className="field-error">{error}</p> : null}
      {issues?.map((issue) => (
        <p className="field-error" key={`${issue.code}-${issue.message}`}>
          {issue.message}
        </p>
      ))}
    </div>
  );
}

function SaveError({ error, issues }: { error: unknown; issues: ApiIssue[] }) {
  const isConflict = error instanceof ApiError && error.kind === "conflict";
  return (
    <div className={isConflict ? "notice notice--warning" : "notice notice--error"} role="alert">
      {isConflict
        ? "This product changed somewhere else, so the form was not saved. The latest values have been reloaded — reapply your edit and save again."
        : error instanceof ApiError
          ? error.userMessage
          : "The changes could not be saved."}
      {issues.length ? (
        <ul>
          {issues.map((issue) => (
            <li key={issue.code}>{issue.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
